import { useState, useEffect } from "react";
import axios from "axios";
import { Bot, CheckCircle, XCircle, AlertTriangle, RefreshCw, Zap } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  mongoProjectId: string;
}

const STATUS_CONFIG: Record<string, { label: string; bg: string; border: string; text: string }> = {
  success: { label: "SUCCESS", bg: "bg-[#b8f5d8]", border: "border-[#2d6a4f]", text: "text-[#1b4332]" },
  failed:  { label: "FAILED",  bg: "bg-[#ffc8c8]", border: "border-[#9b2335]", text: "text-[#7b0d1e]" },
  partial: { label: "PARTIAL", bg: "bg-[#fff3b0]", border: "border-[#7a6000]", text: "text-[#5a4500]" },
};

const STATUS_ICON: Record<string, React.ReactNode> = {
  success: <CheckCircle className="h-3.5 w-3.5" />,
  failed:  <XCircle className="h-3.5 w-3.5" />,
  partial: <AlertTriangle className="h-3.5 w-3.5" />,
};

export default function AgentLogPanel({ mongoProjectId }: Props) {
  const [baProjectId, setBaProjectId] = useState<string | null>(null);
  const [runs, setRuns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  useEffect(() => { init(); }, [mongoProjectId]);

  const init = async () => {
    try {
      const { data } = await axios.get(`${BASE}/api/tools/projects/by-mongo/${mongoProjectId}`);
      setBaProjectId(data.id);
      loadRuns(data.id);
    } catch { setLoading(false); }
  };

  const loadRuns = async (pid: string) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/tools/runs?project_id=${pid}&limit=50`);
      setRuns(data);
    } catch { setRuns([]); }
    finally { setLoading(false); }
  };

  const refresh = () => { if (baProjectId) loadRuns(baProjectId); };
  const toggle = (id: string) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="min-h-full bg-[#fafafa] p-3 font-mono">

      {/* ── HEADER ── */}
      <div className="border-2 border-[#a8c4f0] shadow-[3px_3px_0px_#a8c4f0] bg-[#dce8fb] px-3 py-2 mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="bg-[#2d5bbf] p-1">
            <Bot className="h-3.5 w-3.5 text-white" />
          </div>
          <div>
            <h2 className="text-sm font-black uppercase tracking-tight text-[#1e3a6e] leading-none">Agent Log</h2>
            <p className="text-[10px] font-bold uppercase text-[#2d5bbf]">Full audit trail — every call, every result</p>
          </div>
        </div>
        <button
          onClick={refresh}
          className="border-2 border-[#a8c4f0] bg-white shadow-[2px_2px_0px_#a8c4f0] hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all px-2 py-1 text-[10px] font-black uppercase text-[#1e3a6e] flex items-center gap-1"
        >
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* ── WHY THIS EXISTS ── */}
      <div className="border-2 border-[#a8d8ea] shadow-[2px_2px_0px_#a8d8ea] bg-[#e8f6fb] px-3 py-2 mb-3 text-[10px] font-mono text-[#1a5276]">
        <span className="font-black text-[#2d5bbf]">&gt; WHY_THIS_EXISTS.md</span>
        <br />
        In regulated environments, knowing{" "}
        <span className="font-bold">what an agent did, when, with what input, and what it returned</span>
        {" "}is not optional. <span className="font-bold text-[#2d5bbf]">This log is your audit trail.</span>
      </div>

      {/* ── LOADING ── */}
      {loading && (
        <div className="border-2 border-[#a8c4f0] shadow-[2px_2px_0px_#a8c4f0] bg-white p-6 text-center">
          <div className="text-lg font-black animate-pulse text-[#2d5bbf]">LOADING…</div>
        </div>
      )}

      {/* ── EMPTY ── */}
      {!loading && runs.length === 0 && (
        <div className="border-2 border-[#a8c4f0] shadow-[3px_3px_0px_#a8c4f0] bg-white p-6 text-center">
          <Bot className="h-8 w-8 mx-auto mb-2 text-[#a8c4f0]" />
          <p className="text-sm font-black uppercase text-[#1e3a6e]">No Agent Runs Yet</p>
          <p className="text-[10px] font-bold text-[#2d5bbf] uppercase mt-1">Run a compliance check to see entries here.</p>
        </div>
      )}

      {/* ── RUN LIST ── */}
      {!loading && runs.length > 0 && (
        <div className="space-y-2">
          {/* summary bar */}
          <div className="border-2 border-[#a8c4f0] shadow-[2px_2px_0px_#a8c4f0] bg-white grid grid-cols-3 divide-x-2 divide-[#a8c4f0]">
            {[
              { label: "Total",     value: runs.length,                                          bg: "bg-[#dce8fb]", text: "text-[#1e3a6e]" },
              { label: "Succeeded", value: runs.filter(r => r.status === "success").length,      bg: "bg-[#b8f5d8]", text: "text-[#1b4332]" },
              { label: "Failed",    value: runs.filter(r => r.status === "failed").length,       bg: "bg-[#ffc8c8]", text: "text-[#7b0d1e]" },
            ].map(s => (
              <div key={s.label} className={`${s.bg} px-2 py-1.5 text-center`}>
                <div className={`text-lg font-black ${s.text}`}>{s.value}</div>
                <div className={`text-[9px] font-black uppercase tracking-widest ${s.text}`}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* cards */}
          {runs.map((run: any, i: number) => {
            const cfg = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.partial;
            const isOpen = expanded[run.id];
            return (
              <div key={run.id} className="border-2 border-[#a8c4f0] shadow-[3px_3px_0px_#a8c4f0] bg-white overflow-hidden">
                {/* card header */}
                <div
                  className={`${cfg.bg} border-b-2 border-[#a8c4f0] px-3 py-1.5 flex items-center justify-between cursor-pointer`}
                  onClick={() => toggle(run.id)}
                >
                  <div className="flex items-center gap-2">
                    <div className={`${cfg.text} flex items-center`}>{STATUS_ICON[run.status]}</div>
                    <span className={`text-xs font-black uppercase ${cfg.text}`}>
                      #{runs.length - i} — {run.agent_name}
                    </span>
                    {run.model && (
                      <span className="border border-[#a8c4f0] bg-white text-[#1e3a6e] text-[9px] font-black px-1 py-0.5 uppercase">
                        {run.model}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-bold uppercase ${cfg.text} opacity-70`}>
                      {new Date(run.created_at).toLocaleString()}
                    </span>
                    <span className={`text-xs font-black ${cfg.text}`}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                </div>

                {/* tools row */}
                {run.tools_called?.length > 0 && (
                  <div className="border-b-2 border-[#a8c4f0] px-3 py-1 flex flex-wrap gap-1 bg-[#dce8fb]">
                    <span className="text-[9px] font-black text-[#2d5bbf] uppercase mr-1 self-center">Tools:</span>
                    {run.tools_called.map((t: string) => (
                      <span key={t} className="border border-[#2d5bbf] bg-white text-[#2d5bbf] text-[9px] font-black px-1.5 py-0.5 uppercase flex items-center gap-1">
                        <Zap className="h-2 w-2" />{t}
                      </span>
                    ))}
                  </div>
                )}

                {/* expanded body */}
                {isOpen && (
                  <div className="grid grid-cols-1 md:grid-cols-2 divide-y-2 md:divide-y-0 md:divide-x-2 divide-[#ccdaf5]">
                    {run.input && (
                      <div className="p-2 bg-[#eef4fd]">
                        <div className="text-[9px] font-black uppercase tracking-widest border-b border-[#a8c4f0] pb-1 mb-1 text-[#2d5bbf]">▶ Input</div>
                        <pre className="text-[10px] font-mono text-[#1e3a6e] whitespace-pre-wrap break-all leading-relaxed">
                          {typeof run.input === "object" ? JSON.stringify(run.input, null, 2) : run.input}
                        </pre>
                      </div>
                    )}
                    {run.output && (
                      <div className="p-2 bg-white">
                        <div className="text-[9px] font-black uppercase tracking-widest border-b border-[#a8c4f0] pb-1 mb-1 text-[#2d5bbf]">◀ Output</div>
                        <pre className="text-[10px] font-mono text-[#1e3a6e] whitespace-pre-wrap break-all leading-relaxed">
                          {typeof run.output === "object"
                            ? `passed: ${(run.output as any).passed}\nviolations: ${(run.output as any).violations?.length ?? 0}`
                            : run.output}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* footer */}
                <div className="border-t-2 border-[#a8c4f0] px-3 py-1 bg-[#dce8fb] flex justify-between items-center">
                  <span className="text-[#2d5bbf] text-[9px] font-black uppercase tracking-widest">{cfg.label}</span>
                  {run.duration_ms && (
                    <span className="text-[#1b4332] text-[9px] font-black">{run.duration_ms}ms</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
